import { Component, DestroyRef, EnvironmentInjector, inject, NgZone, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { UserStateService } from './state/user/user-state';
import { PushNotificationService } from './core/services/push-notification.service';
import { AppLanguageService } from './core/services/app-language.service';
import { LoaderComponent } from './shared/ui/loader/loader.component';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { PermissionRequestComponent } from './domains/home/permission-request/permission-request.component';
import { LeatStateService } from './domains/loyalty/state/leat.state';
import { MenuStateService } from './state/menu/menu.state.service';
import { AddressService } from './domains/maps/services/address.service';
import { VenuesStateService } from './state/venues/venues-state.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, firstValueFrom, take } from 'rxjs';

declare var cookieConsentData: any;
declare var cookieConsentCallback: any;

@Component({
	selector: 'app-root',
	templateUrl: 'app.component.html',
	styleUrls: ['app.component.scss'],
	imports: [IonApp, IonRouterOutlet, LoaderComponent],
	standalone: true,
})
export class AppComponent implements OnInit {
	static largeScreen = false;
	static analyticsEnabled = false;

	destroyRef = inject(DestroyRef);
	private router = inject(Router);
	//private analytics = inject(AngularFireAnalytics);
	private platform = inject(Platform);
	private pnSvc = inject(PushNotificationService);
	private zone = inject(NgZone);
	private userStateSvc = inject(UserStateService);
	private venueStateSvc = inject(VenuesStateService);
	// private menuStateSvc = inject(MenuStateService);
	// private leatStateSvc = inject(LeatStateService);
	// private addressSvc = inject(AddressService);
	private injector = inject(EnvironmentInjector);
	protected langSvc = inject(AppLanguageService);

	constructor() {
		console.log(environment.customerGroup + ' ' + environment.version);
	}

	async ngOnInit() {
		setTimeout(() => document.querySelector('#splash-screen').remove(), 2600);
		//this.clickTrackingSvc.init();
		await this.platform.ready();

		PermissionRequestComponent.checkAndRequestPermissions(this.injector);

		await this.keyboardOverlay();
		await this.userStateSvc.userInitialized;
		this.initDeepLinks();
		if (await this.pnSvc.hasPermission()) {
			await this.pnSvc.init();
		}

		//	if (Capacitor.isNativePlatform()) await this.pnSvc.init();
	}

	async keyboardOverlay() {
		if (this.platform.is('android') || !this.platform.is('capacitor')) return;

		const { Keyboard } = await import('@capacitor/keyboard');

		let keyBoardHeight = null;
		let scrollTarget: HTMLElement | null = null;

		const getScrollTarget = (focusedInput: HTMLElement): HTMLElement | null => {
			const modalHost = focusedInput.closest('ion-modal') as HTMLElement | null;
			if (modalHost) {
				let node: HTMLElement | null = focusedInput;
				while (node && node.parentElement !== modalHost) node = node.parentElement;
				return node;
			}
			return document.querySelector<HTMLElement>('.form-wrapper');
		};

		await Keyboard.addListener('keyboardDidShow', async (e): Promise<any> => {
			const focusedInput = document.activeElement as HTMLInputElement;
			if (focusedInput?.type === 'password' && !keyBoardHeight) return (keyBoardHeight = e.keyboardHeight);
			else if (focusedInput?.type === 'password' && keyBoardHeight) keyBoardHeight = e.keyboardHeight;

			if (focusedInput?.tagName !== 'INPUT' && focusedInput?.tagName !== 'TEXTAREA') return;

			scrollTarget = getScrollTarget(focusedInput);
			if (!scrollTarget) return;

			scrollTarget.style.paddingBottom = e.keyboardHeight + 'px';
			setTimeout(() => focusedInput.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50);

			keyBoardHeight = e.keyboardHeight;
		});

		await Keyboard.addListener('keyboardWillHide', () => {
			setTimeout(() => {
				if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
					scrollTarget?.style.setProperty('padding-bottom', '0px');
				}
				keyBoardHeight = null;
			}, 200);
		});
	}

	private async initDeepLinks() {
		if (!this.platform.is('capacitor')) return;
		App.addListener('appUrlOpen', async (event: URLOpenListenerEvent) => {
			const url = new URL(event.url);
			const { InAppBrowser } = await import('@capgo/inappbrowser');

			await InAppBrowser.close().catch(error => {});

			this.zone.run(() => {
				void this.router.navigateByUrl(`${url.pathname}${url.search}${url.hash}`);
			});
		});
	}

	async handleCookieConsent() {
		console.log('handleCookieConsent()');
		AppComponent.analyticsEnabled = cookieConsentData.targeting;

		await this.setAnalyticsEnabled(cookieConsentData.targeting);
	}

	async setAnalyticsEnabled(enabled: boolean) {
		//await this.analytics.setAnalyticsCollectionEnabled(enabled);
	}

	loadHotjarScript() {
		console.log('Load Hotjar');

		// try {
		// 	setTimeout(() => {
		// 		const script = document.createElement('script');
		// 		script.innerHTML = `
		// 		  (function(h,o,t,j,a,r){
		// 			  h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
		// 			  h._hjSettings={hjid:${environment.hjid},hjsv:6};
		// 			  a=o.getElementsByTagName('head')[0];
		// 			  r=o.createElement('script');r.async=1;
		// 			  r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
		// 			  a.appendChild(r);
		// 		  })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
		// 		`;
		// 		document.head.appendChild(script);
		// 	}, 3000);
		// } catch (e) {
		// 	console.log(e);
		// }
	}

	private async initCookieConsent() {
		const onContentLoaded = async () => {
			// просто ініціалізація чи лог
			console.log('Cookie consent data initialized:', cookieConsentData);
		};

		if (document.readyState === 'loading') {
			document.addEventListener('DOMContentLoaded', onContentLoaded);
		} else {
			await onContentLoaded();
		}

		cookieConsentCallback = async () => {
			console.log('Cookie consent callback triggered');
			await this.handleCookieConsent();
		};

		await this.handleCookieConsent();
	}
}
